import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  ArrowLeft,
  DollarSign,
  Users,
  Calendar,
  TrendingUp,
  TrendingDown,
  Edit,
  Phone,
  Hash,
  Gift,
  CheckCircle,
  Clock,
  Target,
  Award,
  User,
  Mail,
  FileText,
  ArrowRight,
  AlertCircle,
  Briefcase,
  Star,
  Crown,
  Trophy,
} from 'lucide-react';

const RANK_CONFIG = {
  agent: { label: 'Agent', color: 'bg-gray-500', icon: User },
  neg: { label: 'NEG', color: 'bg-blue-500', icon: User },
  rm: { label: 'RM', color: 'bg-purple-500', icon: Award },
  avp: { label: 'AVP', color: 'bg-pink-500', icon: Star },
  svp: { label: 'SVP', color: 'bg-orange-500', icon: Crown },
  evp: { label: 'EVP', color: 'bg-red-500', icon: Trophy },
};

const AgentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [rankProgress, setRankProgress] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [commissionClaims, setCommissionClaims] = useState([]);
  const [showEditSheet, setShowEditSheet] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [agentRes, usersRes] = await Promise.all([
        api.get(`/users/${id}`),
        api.get('/users'),
      ]);
      setAgent(agentRes.data);
      setAllUsers(usersRes.data);

      // Load additional data
      try {
        const [rankRes, metricsRes, claimsRes] = await Promise.all([
          api.get(`/agents/${id}/rank-progress`),
          api.get(`/agents/${id}/performance-metrics`),
          api.get(`/agents/${id}/commission-claims`),
        ]);
        setRankProgress(rankRes.data);
        setPerformanceMetrics(metricsRes.data);
        setCommissionClaims(claimsRes.data);
      } catch (err) {
        console.warn('Some additional data failed to load:', err);
      }
    } catch (error) {
      console.error('Error loading agent:', error);
      toast.error('Failed to load agent details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Agent not found</p>
        <Button variant="outline" onClick={() => navigate('/admin/agents')} className="mt-4">
          Back to Agents
        </Button>
      </div>
    );
  }

  const rank = agent.agent_rank || agent.role || 'neg';
  const rankConfig = RANK_CONFIG[rank] || RANK_CONFIG.neg;
  const downlines = allUsers.filter(u => u.upline_id === agent.id);
  const upline = agent.upline_id ? allUsers.find(u => u.id === agent.upline_id) : null;

  // Handle commission history navigation
  const handleCommissionHistory = () => {
    navigate(`/admin/claims?agent_id=${agent.id}`);
  };

  return (
    <div className="p-6 space-y-6" data-testid="agent-details-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/agents')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">Agent Details</h1>
              <Badge className={`${agent.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {agent.status || 'active'}
              </Badge>
            </div>
            <p className="text-sm text-gray-500">{agent.agent_code}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={handleCommissionHistory}
            data-testid="commission-history-btn"
          >
            <Clock className="w-4 h-4 mr-2" />
            Commission History
          </Button>
          <Button 
            className="bg-pink-500 hover:bg-pink-600"
            onClick={() => setShowEditSheet(true)}
            data-testid="edit-agent-btn"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Agent
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left Sidebar - Profile Card */}
        <div className="col-span-3">
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-6 text-center text-white">
              <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4 text-4xl font-bold">
                {agent.full_name?.charAt(0)?.toUpperCase()}
              </div>
              <h2 className="text-xl font-bold">{agent.full_name}</h2>
              <p className="text-white/80 text-sm">{agent.email}</p>
              <Badge className={`${rankConfig.color} text-white mt-3`}>
                {rankConfig.label}
              </Badge>
            </div>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">{agent.phone || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-gray-500">Joined Date</p>
                  <p className="font-medium">{formatDate(agent.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Hash className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-gray-500">Agent Code</p>
                  <p className="font-medium font-mono text-purple-600">{agent.agent_code}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Gift className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-gray-500">Referral Code</p>
                  <p className="font-medium">{agent.referral_code || 'N/A'}</p>
                </div>
              </div>
              {/* Upline Info */}
              <div className="border-t pt-4">
                <p className="text-xs text-gray-500 mb-2">Reports to:</p>
                {upline ? (
                  <div 
                    className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100"
                    onClick={() => navigate(`/admin/agents/${upline.id}`)}
                  >
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                      style={{ backgroundColor: `hsl(${upline.full_name?.charCodeAt(0) * 10 % 360}, 70%, 50%)` }}
                    >
                      {upline.full_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{upline.full_name}</p>
                      <p className="text-xs text-gray-500">{upline.agent_code}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No upline (Top Level)</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="col-span-9 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Earned</p>
                    <p className="text-xl font-bold">RM {(performanceMetrics?.commission?.total_earned || agent.total_commission_earned || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Downlines</p>
                    <p className="text-xl font-bold">{performanceMetrics?.team?.total_downlines || downlines.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">This Year ({new Date().getFullYear()})</p>
                    <p className="text-xl font-bold">RM {(performanceMetrics?.commission?.current_year || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    {(performanceMetrics?.commission?.yoy_growth || 0) >= 0 ? (
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">YoY Growth</p>
                    <p className={`text-xl font-bold ${(performanceMetrics?.commission?.yoy_growth || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(performanceMetrics?.commission?.yoy_growth || 0) >= 0 ? '+' : ''}{performanceMetrics?.commission?.yoy_growth || 0}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="hierarchy">Hierarchy ({downlines.length})</TabsTrigger>
              <TabsTrigger value="claims">Claims ({commissionClaims.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Rank Progress */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Target className="w-4 h-4 text-purple-600" />
                      Rank Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {rankProgress ? (
                      <>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Current Rank</p>
                            <Badge className={`${RANK_CONFIG[rankProgress.current_rank]?.color || 'bg-gray-500'} text-white`}>
                              {rankProgress.current_rank_display}
                            </Badge>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-300" />
                          <div>
                            <p className="text-sm text-gray-500">Next Rank</p>
                            {rankProgress.is_max_rank ? (
                              <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white">Max Rank</Badge>
                            ) : (
                              <Badge className={`${RANK_CONFIG[rankProgress.next_rank]?.color || 'bg-gray-500'} text-white`}>
                                {rankProgress.next_rank_display}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-500">Overall Progress</span>
                            <span className="font-medium text-purple-600">{rankProgress.overall_percentage}%</span>
                          </div>
                          <Progress value={rankProgress.overall_percentage} className="h-3" />
                        </div>

                        {!rankProgress.is_max_rank && rankProgress.requirements && (
                          <div className="space-y-3 pt-2 border-t">
                            <p className="text-xs font-semibold text-gray-600 uppercase">Requirements to Achieve {rankProgress.next_rank_display}</p>
                            
                            {/* Personal Sales */}
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-500">Personal Sales</span>
                                <span className={rankProgress.requirements.personal_sales?.achieved ? 'text-green-600' : 'text-gray-600'}>
                                  RM {(rankProgress.requirements.personal_sales?.current || 0).toLocaleString()} / RM {(rankProgress.requirements.personal_sales?.required || 0).toLocaleString()}
                                </span>
                              </div>
                              <Progress 
                                value={rankProgress.requirements.personal_sales?.percentage || 0} 
                                className="h-2"
                              />
                            </div>

                            {/* Team Commission */}
                            {rankProgress.requirements.team_commission?.required > 0 && (
                              <div className="space-y-1">
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-500">Team Commission</span>
                                  <span className={rankProgress.requirements.team_commission?.achieved ? 'text-green-600' : 'text-gray-600'}>
                                    RM {(rankProgress.requirements.team_commission?.current || 0).toLocaleString()} / RM {(rankProgress.requirements.team_commission?.required || 0).toLocaleString()}
                                  </span>
                                </div>
                                <Progress 
                                  value={rankProgress.requirements.team_commission?.percentage || 0} 
                                  className="h-2"
                                />
                              </div>
                            )}

                            {/* Downline Requirement */}
                            {rankProgress.requirements.downlines?.required > 0 && (
                              <div className="space-y-1">
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-500">
                                    Active {rankProgress.requirements.downlines?.required_rank_display || ''} Downlines
                                  </span>
                                  <span className={rankProgress.requirements.downlines?.achieved ? 'text-green-600' : 'text-gray-600'}>
                                    {rankProgress.requirements.downlines?.current || 0} / {rankProgress.requirements.downlines?.required || 0}
                                  </span>
                                </div>
                                <Progress 
                                  value={rankProgress.requirements.downlines?.percentage || 0} 
                                  className="h-2"
                                />
                              </div>
                            )}
                          </div>
                        )}

                        <div className="flex justify-center gap-6 pt-2">
                          <div className="text-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-1 ${
                              rankProgress.requirements?.personal_sales?.achieved ? 'bg-green-100' : 'bg-gray-100'
                            }`}>
                              {rankProgress.requirements?.personal_sales?.achieved ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <Clock className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                            <span className="text-xs text-gray-500">Sales</span>
                          </div>
                          <div className="text-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-1 ${
                              rankProgress.requirements?.team_commission?.achieved ? 'bg-green-100' : 'bg-gray-100'
                            }`}>
                              {rankProgress.requirements?.team_commission?.achieved ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <Clock className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                            <span className="text-xs text-gray-500">Team</span>
                          </div>
                          <div className="text-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-1 ${
                              rankProgress.requirements?.downlines?.achieved ? 'bg-green-100' : 'bg-gray-100'
                            }`}>
                              {rankProgress.requirements?.downlines?.achieved ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <Clock className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                            <span className="text-xs text-gray-500">Downline</span>
                          </div>
                        </div>

                        {rankProgress.ready_for_promotion && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                            <p className="text-green-700 font-medium text-sm">Ready for Promotion!</p>
                            <p className="text-green-600 text-xs">All requirements met for {rankProgress.next_rank_display}</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>Loading rank progress...</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Yearly Commission Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <DollarSign className="w-4 h-4 text-purple-600" />
                      Yearly Commission History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {performanceMetrics?.yearly_breakdown && Object.keys(performanceMetrics.yearly_breakdown).filter(y => y !== 'cumulative').length > 0 ? (
                      <>
                        <div className="h-48 mb-4">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={Object.entries(performanceMetrics.yearly_breakdown)
                                .filter(([year]) => year !== 'cumulative')
                                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                                .map(([year, amount]) => ({
                                  year,
                                  amount: parseFloat(amount),
                                }))}
                              margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                              <XAxis dataKey="year" stroke="#6B7280" fontSize={12} />
                              <YAxis 
                                stroke="#6B7280" 
                                fontSize={11}
                                tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                              />
                              <Tooltip 
                                formatter={(value) => [`RM ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Commission']}
                                labelFormatter={(label) => `Year ${label}`}
                                contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                              />
                              <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                                {Object.entries(performanceMetrics.yearly_breakdown)
                                  .filter(([year]) => year !== 'cumulative')
                                  .sort(([a], [b]) => parseInt(a) - parseInt(b))
                                  .map((entry, index, arr) => (
                                    <Cell 
                                      key={`cell-${index}`} 
                                      fill={index === arr.length - 1 ? '#8B5CF6' : '#C4B5FD'} 
                                    />
                                  ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        
                        <div className="space-y-2 border-t pt-4">
                          {Object.entries(performanceMetrics.yearly_breakdown)
                            .filter(([year]) => year !== 'cumulative')
                            .sort(([a], [b]) => parseInt(b) - parseInt(a))
                            .slice(0, 4)
                            .map(([year, amount]) => (
                              <div key={year} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-purple-600" />
                                  <span className="font-medium text-sm">{year}</span>
                                </div>
                                <span className="font-semibold text-green-600">
                                  RM {parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                            ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No yearly commission data available</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Account Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <User className="w-4 h-4 text-purple-600" />
                    Account Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-6">
                    <div>
                      <p className="text-sm text-gray-500">Agent Code</p>
                      <p className="font-medium font-mono">{agent.agent_code}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium truncate">{agent.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium">{agent.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Joined Date</p>
                      <p className="font-medium">{formatDate(agent.created_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="performance">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <TrendingUp className="w-4 h-4 text-purple-600" />
                    Performance Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {performanceMetrics ? (
                    <div className="space-y-6">
                      {/* Booking Stats */}
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-3">Bookings</h4>
                        <div className="grid grid-cols-4 gap-4">
                          <div className="bg-gray-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-gray-900">{performanceMetrics.bookings?.total || 0}</p>
                            <p className="text-xs text-gray-500">Total Bookings</p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-green-600">{performanceMetrics.bookings?.completed || 0}</p>
                            <p className="text-xs text-gray-500">Completed</p>
                          </div>
                          <div className="bg-yellow-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-yellow-600">{performanceMetrics.bookings?.pending || 0}</p>
                            <p className="text-xs text-gray-500">Pending</p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-purple-600">{performanceMetrics.bookings?.conversion_rate || 0}%</p>
                            <p className="text-xs text-gray-500">Conversion Rate</p>
                          </div>
                        </div>
                      </div>

                      {/* Claims Stats */}
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-3">Commission Claims</h4>
                        <div className="grid grid-cols-4 gap-4">
                          <div className="bg-gray-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-gray-900">{performanceMetrics.claims?.total || 0}</p>
                            <p className="text-xs text-gray-500">Total Claims</p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-green-600">{performanceMetrics.claims?.paid || 0}</p>
                            <p className="text-xs text-gray-500">Paid</p>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-blue-600">{performanceMetrics.claims?.pending || 0}</p>
                            <p className="text-xs text-gray-500">Pending Review</p>
                          </div>
                          <div className="bg-orange-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-orange-600">{performanceMetrics.claims?.revision_requested || 0}</p>
                            <p className="text-xs text-gray-500">Needs Revision</p>
                          </div>
                        </div>
                      </div>

                      {/* Commission Summary */}
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-3">Commission Summary</h4>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg p-4 text-white">
                            <p className="text-sm opacity-80">Total Earned</p>
                            <p className="text-2xl font-bold">RM {(performanceMetrics.commission?.total_earned || 0).toLocaleString()}</p>
                          </div>
                          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg p-4 text-white">
                            <p className="text-sm opacity-80">Pending Payout</p>
                            <p className="text-2xl font-bold">RM {(performanceMetrics.commission?.pending || 0).toLocaleString()}</p>
                          </div>
                          <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg p-4 text-white">
                            <p className="text-sm opacity-80">Team Commission</p>
                            <p className="text-2xl font-bold">RM {(performanceMetrics.team?.team_commission || 0).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>

                      {/* Team Stats */}
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-3">Team Performance</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-gray-500">Total Downlines</p>
                                <p className="text-2xl font-bold">{performanceMetrics.team?.total_downlines || 0}</p>
                              </div>
                              <Users className="w-10 h-10 text-purple-200" />
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-gray-500">Active Downlines</p>
                                <p className="text-2xl font-bold text-green-600">{performanceMetrics.team?.active_downlines || 0}</p>
                              </div>
                              <CheckCircle className="w-10 h-10 text-green-200" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <TrendingUp className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>Loading performance metrics...</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="hierarchy">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    {upline && (
                      <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">Reports to:</p>
                        <div 
                          className="flex items-center gap-3 cursor-pointer hover:bg-purple-100 p-2 rounded-lg -m-2"
                          onClick={() => navigate(`/admin/agents/${upline.id}`)}
                        >
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                            style={{ backgroundColor: `hsl(${upline.full_name?.charCodeAt(0) * 10 % 360}, 70%, 50%)` }}
                          >
                            {upline.full_name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{upline.full_name}</p>
                            <p className="text-xs text-gray-500">{upline.agent_code} • {(upline.agent_rank || upline.role)?.toUpperCase()}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    Direct Downlines ({downlines.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {downlines.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No downline agents</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {downlines.map((d) => (
                        <div 
                          key={d.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                          onClick={() => navigate(`/admin/agents/${d.id}`)}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                              style={{ backgroundColor: `hsl(${d.full_name?.charCodeAt(0) * 10 % 360}, 70%, 50%)` }}
                            >
                              {d.full_name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium">{d.full_name}</p>
                              <p className="text-sm text-gray-500">{d.agent_code} • {d.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={`${RANK_CONFIG[d.agent_rank || d.role]?.color || 'bg-gray-200'} text-white`}>
                              {(d.agent_rank || d.role)?.toUpperCase()}
                            </Badge>
                            <p className="text-sm text-gray-500 mt-1">
                              RM {(d.total_commission_earned || d.total_earned || 0).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="claims">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileText className="w-4 h-4 text-purple-600" />
                    Commission Claims History
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleCommissionHistory}
                  >
                    View All Claims
                  </Button>
                </CardHeader>
                <CardContent>
                  {commissionClaims.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No commission claims found</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {commissionClaims.slice(0, 10).map((claim) => (
                        <div 
                          key={claim.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                          onClick={() => navigate(`/admin/claims/${claim.id}`)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              claim.status === 'paid' ? 'bg-green-100' :
                              claim.status === 'approved' ? 'bg-blue-100' :
                              claim.status === 'revision_requested' ? 'bg-orange-100' :
                              'bg-gray-100'
                            }`}>
                              <DollarSign className={`w-5 h-5 ${
                                claim.status === 'paid' ? 'text-green-600' :
                                claim.status === 'approved' ? 'text-blue-600' :
                                claim.status === 'revision_requested' ? 'text-orange-600' :
                                'text-gray-600'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium">{claim.claim_reference || claim.id?.slice(0, 8).toUpperCase()}</p>
                              <p className="text-sm text-gray-500">
                                {claim.project_name} • {claim.unit_number}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={`${
                              claim.status === 'paid' ? 'bg-green-100 text-green-700' :
                              claim.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                              claim.status === 'revision_requested' ? 'bg-orange-100 text-orange-700' :
                              claim.status === 'submitted' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {claim.status?.replace('_', ' ')}
                            </Badge>
                            <p className="text-sm font-semibold text-green-600 mt-1">
                              RM {(claim.total_commission || 0).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Edit Agent Sheet */}
      <EditAgentSheet
        open={showEditSheet}
        onOpenChange={setShowEditSheet}
        agent={agent}
        allUsers={allUsers}
        onSave={() => {
          loadData();
          setShowEditSheet(false);
          toast.success('Agent updated successfully');
        }}
      />
    </div>
  );
};

// Edit Agent Sheet Component
const EditAgentSheet = ({ open, onOpenChange, agent, allUsers, onSave }) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (agent) {
      setFormData({
        full_name: agent.full_name || '',
        nickname: agent.nickname || '',
        email: agent.email || '',
        phone: agent.phone || '',
        contact_number: agent.contact_number || '',
        ic_passport: agent.ic_passport || agent.ic_number || '',
        address: agent.address || '',
        bank_name: agent.bank_name || '',
        bank_account: agent.bank_account || '',
        bank_holder_name: agent.bank_holder_name || '',
        mobile_wallet: agent.mobile_wallet || '',
        agent_rank: agent.agent_rank || agent.role || 'neg',
        status: agent.status || 'active',
        upline_id: agent.upline_id || '',
        referred_by_code: agent.referred_by_code || '',
        commission_percentage: agent.commission_percentage || 2.5,
        kyc_status: agent.kyc_status || 'pending',
        full_time_agent: agent.full_time_agent || false,
      });
    }
  }, [agent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${agent.id}`, formData);
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    } finally {
      setSaving(false);
    }
  };

  if (!agent) return null;

  const uplineOptions = allUsers.filter(u => 
    u.id !== agent.id && ['neg', 'rm', 'avp', 'svp', 'evp'].includes(u.role || u.agent_rank)
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-xl">
            <div>Edit Agent</div>
            <div className="text-sm font-normal text-gray-500">
              {agent.agent_code} - {agent.full_name}
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900">
              <User className="w-4 h-4 text-purple-600" />
              Personal Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Full Name *</label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Nickname</label>
                <Input
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                  placeholder="Enter nickname"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Email *</label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Phone *</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Contact Number</label>
                <Input
                  value={formData.contact_number}
                  onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">IC/Passport Number</label>
                <Input
                  value={formData.ic_passport}
                  onChange={(e) => setFormData({ ...formData, ic_passport: e.target.value })}
                  placeholder="Enter IC or passport number"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Address</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter full address"
              />
            </div>
          </div>

          {/* Banking Information */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900">
              <Briefcase className="w-4 h-4 text-purple-600" />
              Banking Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Bank Name</label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="e.g., Maybank"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Bank Account</label>
                <Input
                  value={formData.bank_account}
                  onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                  placeholder="Account number"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Account Holder Name</label>
                <Input
                  value={formData.bank_holder_name}
                  onChange={(e) => setFormData({ ...formData, bank_holder_name: e.target.value })}
                  placeholder="Account holder name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Mobile Wallet</label>
                <Input
                  value={formData.mobile_wallet}
                  onChange={(e) => setFormData({ ...formData, mobile_wallet: e.target.value })}
                  placeholder="e.g., Touch 'n Go"
                />
              </div>
            </div>
          </div>

          {/* Hierarchy & Commission */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900">
              <Users className="w-4 h-4 text-purple-600" />
              Hierarchy & Commission
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Rank</label>
                <Select 
                  value={formData.agent_rank} 
                  onValueChange={(v) => setFormData({ ...formData, agent_rank: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neg">NEG</SelectItem>
                    <SelectItem value="rm">RM</SelectItem>
                    <SelectItem value="avp">AVP</SelectItem>
                    <SelectItem value="svp">SVP</SelectItem>
                    <SelectItem value="evp">EVP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Status</label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-700">Upline</label>
                <Select 
                  value={formData.upline_id || 'none'} 
                  onValueChange={(v) => setFormData({ ...formData, upline_id: v === 'none' ? null : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select upline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Upline (Top Level)</SelectItem>
                    {uplineOptions.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.agent_code} - {u.full_name} ({(u.agent_rank || u.role)?.toUpperCase()})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Referred By Code</label>
                <Input
                  value={formData.referred_by_code}
                  onChange={(e) => setFormData({ ...formData, referred_by_code: e.target.value })}
                  placeholder="Enter referral code"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">KYC Status</label>
                <Select 
                  value={formData.kyc_status} 
                  onValueChange={(v) => setFormData({ ...formData, kyc_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData.full_time_agent}
                onCheckedChange={(checked) => setFormData({ ...formData, full_time_agent: checked })}
              />
              <label className="text-sm font-medium text-gray-700">Full-Time Agent</label>
            </div>
          </div>

          {/* Agent Codes (Read-Only) */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Agent Codes (Read-Only)</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Agent Code:</span>
                <span className="ml-2 font-mono font-semibold text-purple-600">{agent.agent_code}</span>
              </div>
              <div>
                <span className="text-gray-500">Referral Code:</span>
                <span className="ml-2 font-mono">{agent.referral_code || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Update Agent'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AgentDetails;
